import cn from 'classnames';
import { Children, cloneElement, forwardRef, isValidElement } from 'react';

import ButtonGroup from '../button-group/button-group';

import type { ForwardedRef, ReactElement, SyntheticEvent } from 'react';

import type { ToggleButtonProps } from '../toggle-button/toggle-button.types';
import type { ToggleButtonGroupMultipleProps, ToggleButtonGroupSingleProps } from './toggle-button-group.types';

function isValueSelected<T>(value: T | undefined, candidate: T | T[] | undefined): boolean {
  if (candidate === undefined || value === undefined) {
    return false;
  }

  if (Array.isArray(candidate)) {
    return candidate.includes(value);
  }

  return value === candidate;
}

/**
 * The current component is used as an argument for the `forwardRef` function to forward ref.
 * However, to keep the generic interface of the `ToggleButtonGroup` component we have to cast type
 * after wrapping this component with the `forwardRef` function.
 */
function ToggleButtonGroupInner<T extends number | string = string>(
  props: ToggleButtonGroupMultipleProps<T> | ToggleButtonGroupSingleProps<T>,
  forwardedRef: ForwardedRef<HTMLDivElement>,
): ReactElement {
  const { children, className, disabled, multiple = false, onChange, value } = props;

  const handleChange = (event: SyntheticEvent<HTMLButtonElement>, buttonValue: T): void => {
    if (isValueSelected(buttonValue, value)) {
      return;
    }

    (onChange as ToggleButtonGroupSingleProps<T>['onChange'])?.(event, buttonValue);
  };

  const handleMultipleChange = (event: SyntheticEvent<HTMLButtonElement>, buttonValue: T): void => {
    let newValue: T[] = [];

    if (Array.isArray(value)) {
      newValue = [...value];
    }

    const index = newValue.indexOf(buttonValue);

    if (index !== -1) {
      newValue.splice(index, 1);
    } else {
      newValue.push(buttonValue);
    }

    (onChange as ToggleButtonGroupMultipleProps<T>['onChange'])?.(event, newValue);
  };

  const classes = cn(className, 'rbui-toggle-button-group');

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child as ReactElement<ToggleButtonProps<T>>, {
        disabled: disabled || child.props.disabled,
        onClick: multiple ? handleMultipleChange : handleChange,
        selected: isValueSelected<T>(child.props.value, value),
      });
    }

    return child;
  });

  return (
    <ButtonGroup className={classes} ref={forwardedRef} {...props}>
      {childrenWithProps}
    </ButtonGroup>
  );
}

const ToggleButtonGroup = forwardRef(ToggleButtonGroupInner);

ToggleButtonGroup.displayName = 'ToggleButtonGroup';

/**
 * The `ToggleButtonGroup` controls the selected state of its child buttons when given its own `value` prop.
 * To emphasize groups of related toggle buttons, a group should share a common container.
 *
 * ## Usage
 *
 * ### Basic
 *  ```tsx
 *  import { ToggleButton, ToggleButtonGroup } from '@bloomreach/react-banana-ui`;
 *
 *  export default BasicToggleGroup() {
 *    const [value, setValue] = useState('mobile');
 *
 *    return (
 *      <ToggleButtonGroup
 *        value={value}
 *        onChange={(event, value) => { setValue(value); }}
 *      >
 *        <ToggleButton value="web">Web</ToggleButton>
 *        <ToggleButton value="mobile">Mobile</ToggleButton>
 *        <ToggleButton value="desktop">Desktop</ToggleButton>
 *      </ToggleButtonGroup>
 *    );
 *  };
 *  ```
 * The toggle group might be used as a radio button when selecting one option deselects any other
 * it is the default behavior also as multiple selections by passing the `multiple` prop
 * it allows to have multiple options selected.
 *
 * The toggle button group `value` is the currently selected value within the group or
 * an array of selected values when `multiple` is `true`.
 * The value must have reference equality with the option in order to be selected.
 *
 * The `onChange` callback is fired on the value change and has a different signature for the `value` argument.
 * When `multiple` is `true` this is an array of selected values; when `false` is a single value.
 * If no value is selected and  `multiple` is `true` the value is an empty array.
 * In case when `multiple` is `false` then `onChange` the callback doesn't fire
 * if the user clicks on the already selected value.
 *
 * The `ToggleButtonGroup` is generic component and determinate value type
 * based on `value` or it might be explicitly defined like
 *
 * ### Defined value type
 * ```tsx
 * type MyValue = 'one' | 'two' | 'three';
 * <ToggleButtonGroup<MyValue>>...</ToggleButtonGroup>
 * ```
 * by default `value` has a `string` type and `string[]` for `multiple` mode.
 */
export default ToggleButtonGroup as <T extends number | string = string>(
  props:
    | (ToggleButtonGroupMultipleProps<T> & {
        ref?: ForwardedRef<HTMLDivElement>;
      })
    | ToggleButtonGroupSingleProps<T>,
) => ReturnType<typeof ToggleButtonGroupInner<T>>; // Check the comment for `ToggleButtonGroupInner` to get a reason
